class TogetherAIService {
  constructor() {
    this.baseURL = 'https://api.together.xyz/v1';
    this.model = 'meta-llama/Llama-3.2-11B-Vision-Instruct-Turbo';
    this.apiKey = process.env.REACT_APP_TOGETHER_API_KEY;
    this.testMode = process.env.NODE_ENV === 'development' && (!this.apiKey || this.apiKey === 'your_together_ai_api_key_here');
  }

  // Initialize the service with API key validation
  initialize(apiKey = null) {
    if (apiKey) {
      this.apiKey = apiKey;
    }
    
    if (!this.apiKey) {
      console.warn('Together.AI API key not found. Please set REACT_APP_TOGETHER_API_KEY environment variable.');
      return false;
    }
    
    return true;
  }

  // Generate AI chat responses using Apriel 1.6 15B Thinker
  async generateChatResponse(messages, options = {}) {
    // Test mode for development without API key
    if (this.testMode) {
      console.log('Together.AI running in test mode (no API key)');
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000)); // Simulate API delay
      
      const mockResponses = [
        "Here's a strategic suggestion for your campaign based on current market trends and best practices.",
        "I recommend focusing on audience engagement and clear value propositions for maximum impact.",
        "Consider optimizing your content for multiple platforms to maximize reach and effectiveness.",
        "Your campaign strategy looks solid. Here are some enhancements to boost performance.",
        "Based on industry insights, I suggest emphasizing social proof and compelling calls-to-action."
      ];
      
      const randomResponse = mockResponses[Math.floor(Math.random() * mockResponses.length)];
      return {
        success: true,
        response: randomResponse,
        usage: { prompt_tokens: 50, completion_tokens: 30, total_tokens: 80 },
        model: this.model + ' (test mode)'
      };
    }

    if (!this.apiKey) {
      return {
        success: false,
        error: 'API key not configured',
        response: "AI service not configured. Please set up your Together.AI API key."
      };
    }

    try {
      const requestBody = {
        model: this.model,
        messages: messages,
        max_tokens: options.maxTokens || 1000,
        temperature: options.temperature || 0.7,
        top_p: options.topP || 0.9,
        stream: false
      };

      // Add optional parameters if provided
      if (options.stop) requestBody.stop = options.stop;

      console.log('Together.AI request:', {
        url: `${this.baseURL}/chat/completions`,
        model: this.model,
        messageCount: messages.length
      });

      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Together.AI response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error?.message || errorData.message || errorMessage;
        } catch (e) {
          // If we can't parse error JSON, use the HTTP status
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Together.AI response received');

      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid response format from Together.AI');
      }

      return {
        success: true,
        response: data.choices[0].message.content,
        usage: data.usage,
        model: this.model
      };
    } catch (error) {
      console.log('Together.AI API error:', error.message);
      
      // Provide more specific error messages
      let userMessage = "AI service temporarily unavailable.";
      if (error.message.includes('API key')) {
        userMessage = "AI service configuration error.";
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        userMessage = "Network connection error.";
      } else if (error.message.includes('rate limit')) {
        userMessage = "AI service rate limit exceeded.";
      }
      
      return {
        success: false,
        error: error.message,
        response: userMessage
      };
    }
  }

  // Generate AI feedback for campaigns and content
  async generateAIFeedback(content, type = 'general', context = {}) {
    const systemPrompt = this.getSystemPrompt(type);
    const userPrompt = this.buildUserPrompt(content, type, context);

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ];

    return await this.generateChatResponse(messages, {
      maxTokens: 800,
      temperature: 0.6
    });
  }

  // Generate campaign suggestions
  async generateCampaignSuggestion(businessInfo, campaignType, targetAudience) {
    const systemPrompt = `You are an expert marketing strategist specializing in creating effective digital marketing campaigns. Provide actionable, specific recommendations based on the business type and target audience.`;
    
    const userPrompt = `
Create a marketing campaign suggestion for:

Business: ${businessInfo.name || 'Business'}
Industry: ${businessInfo.industry || 'General'}
Campaign Type: ${campaignType}
Target Audience: ${targetAudience}

Please provide:
1. Campaign concept and key message
2. Content ideas (3-4 specific suggestions)
3. Platform recommendations
4. Timing and frequency suggestions
5. Key metrics to track

Keep the response concise and actionable.
`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ];

    return await this.generateChatResponse(messages, {
      maxTokens: 600,
      temperature: 0.7
    });
  }

  // Generate social media post content
  async generatePostContent(prompt, platform, businessContext = {}) {
    const systemPrompt = `You are a social media content expert. Create engaging, platform-optimized content that drives engagement and aligns with brand voice.`;
    
    const platformSpecs = this.getPlatformSpecs(platform);
    const userPrompt = `
Create a ${platform} post for:

Business: ${businessContext.name || 'Business'}
Industry: ${businessContext.industry || 'General'}
Brand Voice: ${businessContext.tone || 'Professional and friendly'}

Content Request: ${prompt}

Platform Guidelines:
${platformSpecs}

Provide only the post content, including suggested hashtags where appropriate.
`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ];

    return await this.generateChatResponse(messages, {
      maxTokens: 300,
      temperature: 0.8
    });
  }

  // Generate content optimization suggestions
  async optimizeContent(content, goals, metrics = {}) {
    const systemPrompt = `You are a content optimization expert. Analyze content and provide specific recommendations to improve performance based on goals and current metrics.`;
    
    const userPrompt = `
Analyze and optimize this content:

Content: "${content}"

Goals: ${goals}
Current Metrics: ${JSON.stringify(metrics, null, 2)}

Provide:
1. Content strengths
2. Areas for improvement
3. Specific optimization suggestions
4. A/B testing recommendations

Keep recommendations actionable and specific.
`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ];

    return await this.generateChatResponse(messages, {
      maxTokens: 500,
      temperature: 0.6
    });
  }

  // Helper method to get system prompts for different types
  getSystemPrompt(type) {
    const prompts = {
      general: 'You are a helpful AI assistant focused on providing clear, actionable business and marketing advice.',
      campaign: 'You are a marketing campaign expert. Provide strategic, data-driven recommendations for campaign optimization.',
      content: 'You are a content strategist. Help create and optimize engaging content that drives results.',
      social: 'You are a social media expert. Provide platform-specific advice for maximizing social media engagement.',
      analytics: 'You are a marketing analytics expert. Help interpret data and provide insights for improvement.'
    };
    
    return prompts[type] || prompts.general;
  }

  // Helper method to build user prompts
  buildUserPrompt(content, type, context) {
    switch (type) {
      case 'campaign':
        return `Analyze this campaign and provide improvement suggestions:\n\nCampaign: ${content}\nContext: ${JSON.stringify(context)}`;
      case 'content':
        return `Review this content and suggest improvements:\n\nContent: ${content}\nGoals: ${context.goals || 'Increase engagement'}`;
      case 'social':
        return `Optimize this social media content:\n\nPost: ${content}\nPlatform: ${context.platform || 'general'}\nAudience: ${context.audience || 'general'}`;
      default:
        return content;
    }
  }

  // Helper method to get platform specifications
  getPlatformSpecs(platform) {
    const specs = {
      instagram: 'Character limit: 2,200. Use 3-5 hashtags. Focus on visuals and storytelling.',
      facebook: 'Optimal length: 40-80 characters for posts. Encourage engagement with questions.',
      twitter: 'Character limit: 280. Use 1-2 hashtags. Be concise and engaging.',
      linkedin: 'Professional tone. 150-300 characters for updates. Focus on value and insights.',
      general: 'Adapt content for the specific platform while maintaining brand voice.'
    };
    
    return specs[platform] || specs.general;
  }

  // Check if the service is properly configured
  isConfigured() {
    return !!this.apiKey || this.testMode;
  }

  // Get current configuration
  getConfig() {
    return {
      model: this.model,
      configured: this.isConfigured(),
      testMode: this.testMode,
      baseURL: this.baseURL
    };
  }
}

const togetherAIService = new TogetherAIService();
export default togetherAIService;